/**
 * Funnel route guard. Children render unconditionally; the session mint
 * runs in the background and surfaces failures as a non-blocking retry
 * banner. See `useFunnelLead.tsx` for the resilience contract.
 *
 * Variant is persisted to sessionStorage so it survives the Stripe 3DS
 * redirect on the payment page.
 */

import { POSITIONING_VARIANT_STORAGE_KEY } from '@/constants/funnel';
import { useEnsureFunnelSession } from '@/hooks/useFunnelLead';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useFeatureFlags, VariantFlags } from '@/services/feature-flag-provider';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useFunnelVariant } from './routes';

interface FunnelGuardProps {
  children?: ReactNode;
}

export function FunnelGuard({ children }: FunnelGuardProps) {
  const variant = useFunnelVariant();

  useEffect(() => {
    sessionStorage.setItem(POSITIONING_VARIANT_STORAGE_KEY, variant);
  }, [variant]);

  const { error, retry, isInitializing } = useEnsureFunnelSession(variant);

  const { setPositioningVariantProperty, setPriceVariantProperty } = useAnalyticsCalls();
  const hasSetPropRef = useRef(false);
  useEffect(() => {
    if (!hasSetPropRef.current) {
      hasSetPropRef.current = true;
      setPositioningVariantProperty(variant);
    }
  }, [variant, setPositioningVariantProperty]);

  // Price experiment: attach the resolved variant's *price* (from the
  // Amplitude payload, not the bucket label) as a user property so funnel
  // + payment events segment cleanly by what the user saw. Amplitude is
  // source of truth — shared device_id (cookie set on LP) means we get
  // the same bucket as the marketing page. See
  // docs/specs/price-test-99-vs-199.md.
  const { getVariantPayload, isLoading: isFlagsLoading } = useFeatureFlags();
  const hasSetPriceRef = useRef(false);
  useEffect(() => {
    if (hasSetPriceRef.current || isFlagsLoading) return;
    const payload = getVariantPayload(VariantFlags.PricingExperiment) as { price?: number } | undefined;
    if (payload?.price === undefined) return;
    hasSetPriceRef.current = true;
    setPriceVariantProperty(String(payload.price));
  }, [isFlagsLoading, getVariantPayload, setPriceVariantProperty]);

  return (
    <>
      {error && !isInitializing && <FunnelSessionErrorBanner onRetry={retry} />}
      {children ?? <Outlet />}
    </>
  );
}

interface FunnelSessionErrorBannerProps {
  onRetry: () => void;
}

function FunnelSessionErrorBanner({ onRetry }: FunnelSessionErrorBannerProps) {
  return (
    <div role="status" className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
        <span>Having trouble connecting. Your info is safe — keep going and we’ll retry.</span>
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-amber-300 bg-white px-3 py-1 font-medium text-amber-900 hover:bg-amber-100"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
